/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  OnDestroy,
  Renderer2,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, combineLatest, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, take, takeUntil} from 'rxjs/operators';

import cloneDeep from 'lodash-es/cloneDeep';

import * as moment from 'moment/moment';

import {DATE_FORMAT, DATE_MAX, DATE_MIN} from '../../../../app.constants';
import {Destroyable} from '../../../../destroyable.component';
import {EmpContractAttributes} from '../../../../models/models.interfaces';
import {Data} from '../../../api';
import {LocalizationService} from '../../../localization/localization.service';
import {ModalService} from '../../../modal';
import {DateLocalizationPipe} from '../../../pipes/date-localization.pipe';
import {isPresent} from '../../../utils/lang';
import {Strings} from '../../../utils/strings';
import {EmpZoneDependencyUtils} from '../../emp-zone-dependency.utils';
import {DefaultValue} from '../../form-card/form-card-field/form-card-field.component';
import {
  ControlAndZoneDescriptor,
  EmpZoneDependency,
  GroupZoneDescriptor,
  ZoneDescriptor,
  ZoneValues
} from '../../form-generator.interfaces';
import {FormGeneratorService} from '../../form-generator.service';
import {FormGeneratorUtils} from '../../form-generator.utils';
import {FormTableCellComponent} from '../form-table-cell/form-table-cell.component';

@Component({
  selector: 'tr [gp-form-table-row] .table-body',
  templateUrl: 'form-table-row.component.html',
  styleUrls: ['./form-table-row.component.scss'],
  providers: [
    DateLocalizationPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormTableRowComponent extends Destroyable implements AfterViewInit, OnDestroy {

  public loaded: EventEmitter<Array<ControlAndZoneDescriptor>> = new EventEmitter<Array<ControlAndZoneDescriptor>>();
  public cardDescriptor: GroupZoneDescriptor;
  public cellDescriptors: any;
  public dataIndex: number;
  public tableData: any;

  public tableCells: Array<ComponentRef<FormTableCellComponent>> = [];
  public data: ZoneValues;
  public readOnly: boolean = false;
  public locale: string;
  public currency: string;
  public contract: Data<EmpContractAttributes>;
  public canDelete: boolean = false;
  public groupZoneKeyName: string;
  public isGroupCollapserVisible: boolean = false;
  public isGroupCollapserDisabled: boolean = false;
  public isGroupCollapserCollapsed: boolean = false;
  public datasByGroupZoneKeyValue: ZoneValues[];

  public beginDateValue: string;
  public endDateValue: string;
  public beginDateControl: UntypedFormControl = new UntypedFormControl('', this.datesValidation.bind(this));
  public endDateControl: UntypedFormControl = new UntypedFormControl('', this.datesValidation.bind(this));
  public previousDate: {
    begindate: string;
    enddate: string;
  } = {begindate: '', enddate: ''};
  public previousDatePeopleTree: {
    beginDate: string;
    endDate: string;
  } = {beginDate: '', endDate: ''};
  public minDate: Date = new Date(DATE_MIN);
  public maxDate: Date = new Date(DATE_MAX);

  public dateFormat: string = DATE_FORMAT;
  public onEdit: EventEmitter<any> = new EventEmitter();

  public editMode: boolean = false;
  public isNewTableRow: boolean = false;
  public wasEditModeBeforeReload: boolean = false;
  public areRowsReloaded: boolean = false;
  public focusedCellId: string;

  public switchEditMode: EventEmitter<boolean> = new EventEmitter<boolean>();
  public onActiveRow: EventEmitter<number> = new EventEmitter<number>();

  public group: UntypedFormGroup;
  public errorText: string;
  public isOnBoarding = false;
  public employeeId: number = null;
  @ViewChild('tableCell', {read: ViewContainerRef, static: true})
  private tableCell: ViewContainerRef;
  private removeClickOutsideListener: Function;

  private dependencySubscriptions: Subscription[] = [];

  constructor(private componentFactoryResolver: ComponentFactoryResolver,
              private changeDetectorRef: ChangeDetectorRef,
              private formBuilder: UntypedFormBuilder,
              private el: ElementRef,
              private renderer: Renderer2,
              private formGeneratorService: FormGeneratorService,
              private modalService: ModalService,
              private translate: TranslateService,
              private localization: LocalizationService,
              private dateLocalizationPipe: DateLocalizationPipe,
              private empZoneDependencyUtils: EmpZoneDependencyUtils) {
    super();
  }

  public setBackgroundColor(styleValue: string) {
    this.renderer.setStyle(this.el.nativeElement, 'backgroundColor', styleValue);
  }

  public setDisplay(displayValue: string) {
    this.renderer.setStyle(this.el.nativeElement, 'display', displayValue);
  }

  public ngAfterViewInit(): any {
    this.loadFields()
      .then(fields => {
        this.loaded.emit(fields);
        this.loaded.complete();
        if (this.isNewTableRow || this.wasEditModeBeforeReload) {
          this.openEditMode();
        }

        combineLatest(
          [this.beginDateControl.valueChanges.pipe(distinctUntilChanged()),
            this.endDateControl.valueChanges.pipe(distinctUntilChanged())]
        )
          .pipe(
            debounceTime(100)
          )
          .subscribe(([beginDate, endDate]: string[]) => this.onChangeDate(beginDate, endDate));

        this.registerDependencies();
      });
    this.changeDetectorRef.detectChanges();
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.tableCells.forEach(ref => {
      ref.destroy();
      ref = null;
    });
    this.tableCells = [];
    if (this.removeClickOutsideListener) {
      this.removeClickOutsideListener();
    }
  }

  public loadFields(): Promise<any> {
    // Remove existing fields if any
    this.ngOnDestroy();
    this.beginDateValue = this.getBeginDate();
    this.endDateValue = this.getEndDate();
    if (this.isPeopleTree()) {
      this.previousDatePeopleTree = {
        beginDate: this.beginDateValue,
        endDate: this.endDateValue
      };
    }

    // Load field components
    const promisesOfLoadedObservables = this.cellDescriptors.map((zone) => {
      const cellRef: ComponentRef<FormTableCellComponent> = this.tableCell.createComponent(
        this.componentFactoryResolver.resolveComponentFactory(FormTableCellComponent));

      this.changeDetectorRef.markForCheck();
      const cell: FormTableCellComponent = cellRef.instance;
      this.tableCells.push(cellRef);
      cell.descriptor = zone;
      cell.row = this;
      cell.tableData = this.tableData;
      cell.value = this.data.values[zone.zoneID] || new DefaultValue();
      cell.groupZoneDescriptor = this.cardDescriptor;
      cell.readOnly = this.readOnly || zone.readOnly;
      cell.currency = this.currency;
      cell.locale = this.locale;
      cell.groupData = this.data;
      cell.isValueHidden = this.groupZoneKeyName
        && !this.isGroupCollapserVisible
        && zone.zoneID === this.groupZoneKeyName
        && this.datasByGroupZoneKeyValue.length !== 1;
      cell.isKey = zone.zoneID === this.groupZoneKeyName;
      cell.cellFocus = this.cellFocus.bind(this);
      cell.focused = this.isNewTableRow
        ? this.cellDescriptors[0].zoneID === zone.zoneID
        : zone.zoneID === this.focusedCellId;
      cell.isOnBoarding = this.isOnBoarding;
      cell.employeeId = this.employeeId;

      return cell.loaded.asObservable()
        .pipe(
          map(control => {
            return {
              control,
              descriptor: cell.descriptor
            };
          })
        ).toPromise();
    });

    return Promise.all(promisesOfLoadedObservables)
      .then(controls => {
        const all: Array<ControlAndZoneDescriptor> = [].concat.apply([], controls);

        const group: any = all.reduce((result, item) => {
          // Replace the zoneID '.' (dot) of linked entities (like cliEmp.firstName) because it breaks angular
          result[FormGeneratorUtils.sanitizeZoneName(item.descriptor.zoneID)] = item.control;
          return result;
        }, {});

        if (this.cardDescriptor.isDated) {
          const isDatesDirty: boolean = this.beginDateControl.dirty || this.endDateControl.dirty;
          group.begindate = this.beginDateControl;
          group.enddate = this.endDateControl;
          this.beginDateValue = this.getBeginDate();
          this.endDateValue = this.getEndDate();
          this.beginDateControl.setValue(this.beginDateValue);
          this.endDateControl.setValue(this.endDateValue);

          if (!isDatesDirty) {
            setTimeout(() => {
              this.beginDateControl.markAsPristine();
              this.endDateControl.markAsPristine();
              this.changeDetectorRef.markForCheck();
            }, 0);
          }
        }

        if (this.group) {
          Object.keys(group).forEach(key => {
            this.group.setControl(key, group[key]);
          });
        } else {
          this.group = this.formBuilder.group(group);
        }

        this.changeDetectorRef.markForCheck();
        return this.group;
      });
  }

  public reLoadRows(rowIndex: number, cellFocusedId?: string) {
    /* fake function. Will be overrided inside the  form-table-component */
  }

  public cloneEntity(index: number, event) {
    /* fake function. Will be overrided inside the  form-table-component */
  }

  public removeEntity(index: number, event) {
    /* fake function. Will be overrided inside the  form-table-component */
  }

  public expandCollapseGroup(index: number, event) {
    /* fake function. Will be overrided inside the  form-table-component */
  }

  public isGroupCollapsed(index: number) {
    /* fake function. Will be overrided inside the  form-table-component */
  }

  public addClickOutsideListener() {
    this.removeClickOutsideListener = this.renderer.listen('document', 'click', (event) => {
      if (event.target && document.contains(event.target) && !this.el.nativeElement.contains(event.target)
      ) {
        this.toggleCardEdit(false);
      }
    });
  }

  public toggleCardEdit(isEditMode: boolean, isBackToEditMode?: boolean) {
    if (this.readOnly || this.data.default) {
      return;
    }

    if (this.editMode !== isEditMode) {

      // When you click to a row clickedTable$ is updated with the groupZoneId .We need that in the case when
      // in the same groupZone we have to close a row to tell him that another one is opening and he don't need
      // to do the reload

      this.formGeneratorService.clickedTable$
        .pipe(
          take(1)
        )
        .subscribe(value => {
          this.areRowsReloaded = value === this.cardDescriptor.groupZoneID;
        });


      if (isEditMode) {
        // Here we are in the case of a row that is passing in edit mode, we make a setTimeout because we have to let
        // the row that has to close edit mode to execute first

        setTimeout(() => {
          if (!isBackToEditMode && !this.wasEditModeBeforeReload && this.groupZoneKeyName) {
            this.reLoadRows(this.getDataIndexAfterSort(), this.focusedCellId);
            return;
          }
          this.openEditMode();
        });
      } else {
        // Here we are in the case of a row that is closing edit mode so we change the editmode to false but the reload
        // we have to wait to see if there is not another one who is opening the edit mode and so not do the reload
        // because it is always the row who is passing in editmode true that do the reload.If there wasn't a reload
        // we will do it here.

        if (this.editMode && this.removeClickOutsideListener) {
          this.removeClickOutsideListener();
        }
        setTimeout(() => {
          if (this.groupZoneKeyName) {
            if (!this.modalService.modalWithIdOpen.includes('blockingErrorResolutionModal')
              && !this.areRowsReloaded) {
              this.reLoadRows(null);
            }
          } else {
            this.changeEditMode(isEditMode);
          }
          this.formGeneratorService.clickedTable$.next(null);
        });
      }
    }

    // So for this method when we have 2 rows one that is opening (number 1) and one that is closing(number 2)
    // the steps are: number 2 is closing edit mode then number 1 make a reload then number 1 is opening edit mode and
    // number 2 checks if there was a reload and do nothing because number 1 already reloaded
  }

  public changeEditMode(isEditMode) {
    this.editMode = isEditMode;
    this.changeDetectorRef.markForCheck();
    this.switchEditMode.next(isEditMode);
  }


  public openEditMode() {
    this.changeEditMode(true);
    if (this.isNewTableRow) {
      this.isNewTableRow = false;
    }
    this.addClickOutsideListener();
    this.onActiveRow.next(this.dataIndex);
    this.wasEditModeBeforeReload = false;
    this.formGeneratorService.clickedTable$.next(null);
  }

  public displayDate(date: Date | string) {
    date = moment(date).format(DATE_FORMAT);
    if (date === DATE_MAX || date === DATE_MIN) {
      return '';
    }
    return moment(date).format(this.localization.dateFormat);
  }

  public datesValidation(): any {
    let errors = {};
    this.errorText = null;

    if (!isPresent(this.data)) {
      return null;
    }

    if (!isPresent(this.data.begindate) || !isPresent(this.data.enddate)) {
      errors['dated-block-not-filled-dates'] = true;
    } else {
      const beginDate = moment(this.beginDateValue, DATE_FORMAT, true),
        endDate = moment(this.endDateValue, DATE_FORMAT, true);
      if (!beginDate.isValid() || !endDate.isValid()) {
        errors['dated-block-not-filled-dates'] = true;
        return errors;
      }
      if (beginDate.isAfter(endDate)) {
        errors['dated-block-add-begin-after-end'] = true;
        this.errorText = 'dated-block-add-begin-after-end';
      }
    }

    if (!Object.keys(errors).length) {
      errors = null;
    }
    return errors;
  }

  public keyValidation(): any {
    let errors = {};

    if (!isPresent(this.data)) {
      return null;
    }

    errors['block-with-key-null-key-value'] = {
      status: true
    };

    if (!Object.keys(errors).length) {
      errors = null;
    }
    return errors;
  }

  public timeLineValidation(): any {
    /* fake function. Will be overrided inside the  form-table-component */
  }

  public updateDate(newValue: string, dateType: string): void {
    if (this.group) {
      this.previousDate.begindate = this.data.begindate;
      this.previousDate.enddate = this.data.enddate;

      if (dateType === 'enddate') {
        if (newValue === '' || moment(newValue).isAfter(DATE_MAX)) {
          this.setMaxEndDate(newValue !== '');
          return;
        }
      } else if (dateType === 'begindate') {
        if (newValue === '' || moment(newValue).isBefore(DATE_MIN)) {
          this.setMinBeginDate(newValue !== '');
          return;
        }
      }

      this.data[dateType] = newValue;
      this.updateDatesValidity();
    }
  }

  public onChangeDate(beginDate: string, endDate: string) {
    if (this.isPeopleTree() && beginDate && endDate) {
      if (moment(beginDate).isBefore(this.cardDescriptor.metadatas.dateRange.minimumDate)) {
        this.modalService.alert({
          title: this.translate.instant('COMMON.alert'),
          text: this.cardDescriptor.internalType === 'PEOPLE_TREE_POSITION'
            ? this.translate.instant('DIALOG.entity_before_begin_date')
            : this.translate.instant('DIALOG.employee_before_earliest_contract')
        }).then(() => {
          this.setPreviousPeopleTreeDate();
        });
      } else if (moment(endDate).isAfter(this.cardDescriptor.metadatas.dateRange.maximumDate)) {
        this.modalService.alert({
          title: this.translate.instant('COMMON.alert'),
          text: this.cardDescriptor.internalType === 'PEOPLE_TREE_POSITION'
            ? this.translate.instant('DIALOG.entity_after_end_date')
            : this.translate.instant('DIALOG.employee_after_last_contract')
        }).then(() => {
          this.setPreviousPeopleTreeDate();
        });
      } else {
        const cellDescriptor: ZoneDescriptor = this.cellDescriptors
          .find(element => element.type === 'PEOPLE_TREE');
        const item = cellDescriptor.metadatas ? (cellDescriptor.metadatas.items || [])
          .find(element => element.id === this.group.controls[cellDescriptor.zoneID].value) : undefined;
        if (isPresent(item)
          && ((!this.isBetween(beginDate, item.beginDate, item.endDate))
            || (!this.isBetween(endDate, item.beginDate, item.endDate)))) {
          this.modalService.alert({
            title: this.translate.instant('COMMON.alert'),
            text: this.translate.instant('DIALOG.assign_manager_only_from', {
              name: `${item.nodeName}`,
              begin: this.dateLocalizationPipe.transform(item.beginDate),
              end: this.dateLocalizationPipe.transform(item.endDate),
              description: item.description || ''
            })
          }).then(() => {
            this.setPreviousPeopleTreeDate();
          });
        } else {
          this.previousDatePeopleTree.beginDate = beginDate;
          this.previousDatePeopleTree.endDate = endDate;
        }
      }
    }
  }

  public updateDatesValidity() {
    this.beginDateControl.updateValueAndValidity();
    this.endDateControl.updateValueAndValidity();
  }

  public getBeginDate() {
    if (!this.data) {
      return moment().format(DATE_FORMAT);
    }

    if (this.isPeopleTree()
      && moment(this.data.begindate).isSame(DATE_MIN)) {
      const date = this.cardDescriptor.metadatas.dateRange.minimumDate;
      this.data['begindate'] = date;
      return date;
    }

    if (moment(DATE_MIN).isAfter(moment(this.data.begindate))) {
      return DATE_MIN;
    }

    return this.data.begindate;
  }

  public setMinBeginDate(notification: boolean = false) {
    if (notification && !this.isPeopleTree()) {
      this.modalService.dialogFromData(
        'begin-date-before-min',
        this.cardDescriptor.transId
      )
        .then(() => this.setMinBeginDate(false));

      return;
    }

    setTimeout(() => {
      this.beginDateValue = this.isPeopleTree()
        ? this.cardDescriptor.metadatas.dateRange.minimumDate
        : DATE_MIN;
      this.changeDetectorRef.markForCheck();
    });
  }

  public getEndDate() {
    if (!this.data) {
      return moment().format(DATE_FORMAT);
    }

    if (this.isPeopleTree()
      && moment(this.data.enddate).isSame(DATE_MAX)) {
      const date = this.cardDescriptor.metadatas.dateRange.maximumDate;
      this.data['enddate'] = date;
      return date;
    }

    if (moment(DATE_MAX).isBefore(moment(this.data.enddate))) {
      return DATE_MAX;
    }

    return this.data.enddate;
  }

  public setMaxEndDate(notification: boolean = false) {
    if (notification && !this.isPeopleTree()) {
      this.modalService.dialogFromData(
        'end-date-after-max',
        this.cardDescriptor.transId
      )
        .then(() => this.setMaxEndDate(false));

      return;
    }

    setTimeout(() => {
      this.endDateValue = this.isPeopleTree()
        ? this.cardDescriptor.metadatas.dateRange.maximumDate
        : DATE_MAX;
      this.changeDetectorRef.markForCheck();
    });
  }

  public setPreviousPeopleTreeDate() {
    this.beginDateValue = this.previousDatePeopleTree.beginDate;
    this.endDateValue = this.previousDatePeopleTree.endDate;
  }

  public isPeopleTree(): boolean {
    return this.cardDescriptor.internalType === 'PEOPLE_TREE'
      || this.cardDescriptor.internalType === 'PEOPLE_TREE_POSITION';
  }

  public isBetween(verifiableDate: string, afterDate: string, beforeDate: string): boolean {
    return moment(verifiableDate).isSameOrAfter(afterDate)
      && moment(verifiableDate).isSameOrBefore(beforeDate);
  }

  public cellFocus(zoneId: string) {
    this.focusedCellId = zoneId;
  }

  private getDataIndexAfterSort() {
    if (this.tableData.length > 1) {
      let tableDataCopy = cloneDeep(this.tableData).map((data, index) => {
        return {...data, index: index};
      });

      tableDataCopy = tableDataCopy.sort((data1, data2) => {
        const data1KeyValue = data1.values[this.cardDescriptor.groupZoneKeyName].value || '';
        const data2KeyValue = data2.values[this.cardDescriptor.groupZoneKeyName].value || '';
        const keyValueComparison = Strings.toString(data1KeyValue).localeCompare(Strings.toString(data2KeyValue));
        if (keyValueComparison === 0) {
          if (this.cardDescriptor.isDated) {
            if (moment(data1.enddate).isAfter(moment(data2.enddate))) {
              return -1;
            } else if (moment(data1.enddate).isBefore(moment(data2.enddate))) {
              return 1;
            } else {
              return 0;
            }
          } else {
            return 0;
          }
        }
        return keyValueComparison;
      });
      return tableDataCopy.indexOf(tableDataCopy.find(data => data.index === this.dataIndex));
    } else {
      return this.dataIndex;
    }
  }

  @HostBinding('class.pointer')
  public get isEditMode() {
    return !this.editMode;
  }

  @HostBinding('class.card-has-errors')
  public get isValid() {
    return this.group && this.group.invalid;
  }

  @HostListener('click')
  public onClickInside() {
    if (!this.editMode) {
      this.formGeneratorService.clickedTable$.next(this.cardDescriptor.groupZoneID);
    }
    this.toggleCardEdit(true);
  }

  private registerDependencies() {
    this.dependencySubscriptions?.forEach(subscription => subscription.unsubscribe());
    this.dependencySubscriptions = this.empZoneDependencyUtils.registerDependenciesOnOneGroup(
      this.cardDescriptor,
      this.group,
      target => this.findDependencyTriggers(target)
    )
      ?.map(dependencyObservable => dependencyObservable.pipe(takeUntil(this.componentDestroyed$)))
      .map(dependencyObservable => dependencyObservable.subscribe());
  }

  private findDependencyTriggers(target: string): BehaviorSubject<EmpZoneDependency>[] {
    return this.tableCells
      ?.flatMap(fieldRow => fieldRow.instance)
      .filter(instance => instance.descriptor.zoneID === target)
      .map(instance => instance.dependencyTriggered$);
  }

}
